import React, { useState, useEffect, useRef, useCallback } from "react";
import { Loader2, Clock, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Check, X, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { quizApi } from "@/lib/apiClient";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

const Ring = ({ value, label }) => {
    const size = 120, stroke = 10, r = (size - stroke) / 2, c = 2 * Math.PI * r;
    const off = c - (Math.max(0, Math.min(100, value)) / 100) * c;
    const color = value >= 80 ? "hsl(152,52%,46%)" : value >= 60 ? "hsl(206,70%,52%)" : "hsl(38,92%,58%)";
    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--surface-3))" strokeWidth={stroke} />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" style={{ transition: "stroke-dashoffset 1s ease" }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="font-sans-display text-3xl font-semibold text-t1">{value}</span>
                <span className="mono-label text-t4">{label}</span>
            </div>
        </div>
    );
};

const FbLine = ({ color, label, text }) => (
    <div className="flex gap-2">
        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: color }} />
        <p className="text-xs text-t2"><span className="font-medium text-t1">{label}: </span>{text}</p>
    </div>
);

export const QuizRunner = ({ docId, config, onExit, onScored, generateFn }) => {
    const { t } = useTranslation();
    const [phase, setPhase] = useState("generating"); // generating | running | submitting | results
    const [quiz, setQuiz] = useState(null);
    const [idx, setIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    const [result, setResult] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(null);
    const startRef = useRef(Date.now());
    const submittedRef = useRef(false);

    const submit = useCallback(async () => {
        if (submittedRef.current || !quiz) return;
        submittedRef.current = true;
        setPhase("submitting");
        const payload = quiz.questions.map((q) => ({ question_id: q.id, answer: answers[q.id] ?? null }));
        try {
            const res = await quizApi.submit(quiz.id, payload, Math.round((Date.now() - startRef.current) / 1000));
            setResult(res);
            setPhase("results");
            onScored?.(res);
        } catch (e) {
            toast.error(t("quiz.submitFailed"));
            submittedRef.current = false;
            setPhase("running");
        }
    }, [quiz, answers, onScored, t]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const q = generateFn ? await generateFn(config) : await quizApi.generate(docId, config);
                if (cancelled) return;
                setQuiz(q);
                startRef.current = Date.now();
                if (q.time_limit_sec) setSecondsLeft(q.time_limit_sec);
                setPhase("running");
            } catch (e) {
                toast.error(e?.response?.data?.detail || t("quiz.genFailed"));
                onExit?.();
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Countdown timer for exams
    useEffect(() => {
        if (secondsLeft == null || phase !== "running") return;
        if (secondsLeft <= 0) { submit(); return; }
        const tm = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
        return () => clearTimeout(tm);
    }, [secondsLeft, phase, submit]);

    if (phase === "generating") {
        return (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-surface-1 py-20">
                <Loader2 className="h-7 w-7 animate-spin text-[hsl(var(--primary))]" />
                <p className="mt-4 text-sm text-t3">{config.mode === "exam" ? t("quiz.craftingExam") : t("quiz.craftingQuiz")}</p>
            </div>
        );
    }

    if (phase === "results" && result) {
        return (
            <div data-testid="quiz-results">
                <div className="flex flex-col items-center rounded-2xl border border-border bg-surface-1 p-6 text-center">
                    <Ring value={result.score} label={t("quiz.score")} />
                    <p className="mt-3 font-sans-display text-lg font-semibold text-t1">{t("quiz.correctCount", { correct: result.correct_count, total: result.total })}</p>
                    {result.weak_topics?.length > 0 ? (
                        <div className="mt-3">
                            <div className="mono-label mb-1.5 text-t4">{t("quiz.recommendedReview")}</div>
                            <div className="flex flex-wrap justify-center gap-1.5">
                                {result.weak_topics.map((tp, i) => <span key={i} className="rounded-full border border-[hsl(var(--warning))]/40 bg-surface-2 px-2.5 py-1 text-xs text-t2">{tp}</span>)}
                            </div>
                        </div>
                    ) : <p className="mt-2 text-sm text-[hsl(var(--success))]">{t("quiz.noWeak")}</p>}
                    <div className="mt-5 flex gap-2">
                        <Button data-testid="quiz-retake-button" variant="secondary" className="gap-2 bg-surface-3/60" onClick={onExit}><RotateCcw className="h-4 w-4" /> {t("quiz.done")}</Button>
                    </div>
                </div>

                <div className="mt-4 space-y-3">
                    {quiz.questions.map((q, i) => {
                        const pq = result.per_question.find((p) => p.question_id === q.id) || {};
                        const ok = pq.correct;
                        return (
                            <div key={q.id} className="rounded-2xl border border-border bg-surface-1 p-4">
                                <div className="flex items-start gap-2">
                                    {ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--success))]" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--destructive))]" />}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-t1">{i + 1}. {q.question}</p>
                                        {q.type === "mcq" && (
                                            <div className="mt-2 space-y-1">
                                                {q.options.map((o, oi) => (
                                                    <div key={oi} className={cn("rounded-lg px-3 py-1.5 text-sm", oi === pq.correct_index ? "bg-[hsl(var(--success))]/15 text-t1" : oi === pq.user_answer ? "bg-[hsl(var(--destructive))]/15 text-t2" : "text-t3")}>{o}</div>
                                                ))}
                                            </div>
                                        )}
                                        {q.type === "true_false" && <p className="mt-1 text-xs text-t3">{t("quiz.correctAnswer")} <span className="text-t1">{String(pq.correct_bool)}</span></p>}
                                        {["open", "scenario", "case_study", "exam"].includes(q.type) && (
                                            <div className="mt-2 space-y-1.5 text-sm">
                                                <div className="rounded-lg bg-surface-2 p-2.5"><span className="mono-label text-t4">{t("quiz.yourAnswer")} ({pq.score ?? 0}/100)</span><p className="mt-1 text-t2">{pq.user_answer || "—"}</p></div>
                                                {pq.what_correct && <FbLine color="hsl(152,52%,46%)" label={t("pro.whatCorrect")} text={pq.what_correct} />}
                                                {pq.what_missing && <FbLine color="hsl(38,92%,58%)" label={t("pro.whatMissing")} text={pq.what_missing} />}
                                                {pq.what_wrong && <FbLine color="hsl(0,72%,58%)" label={t("pro.whatWrong")} text={pq.what_wrong} />}
                                                {pq.examiner_note && <FbLine color="hsl(206,70%,55%)" label={t("pro.examinerNote")} text={pq.examiner_note} />}
                                                {pq.how_to_improve && <FbLine color="hsl(174,72%,46%)" label={t("pro.howToImprove")} text={pq.how_to_improve} />}
                                                {pq.common_mistake && <FbLine color="hsl(330,62%,64%)" label={t("pro.commonMistake")} text={pq.common_mistake} />}
                                                {pq.advice && <FbLine color="hsl(150,52%,52%)" label={t("pro.advice")} text={pq.advice} />}
                                                {!pq.what_correct && pq.feedback && <p className="text-xs text-t3">{pq.feedback}</p>}
                                                {pq.model_answer && <div className="rounded-lg bg-surface-2 p-2.5"><span className="mono-label text-t4">{t("quiz.modelAnswer")}</span><p className="mt-1 text-t2">{pq.model_answer}</p></div>}
                                            </div>
                                        )}
                                        {q.explanation && <p className="mt-2 text-xs text-t4">{q.explanation}</p>}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // running / submitting
    const q = quiz.questions[idx];
    const a = answers[q.id];
    const setA = (val) => setAnswers((s) => ({ ...s, [q.id]: val }));
    const answeredCount = quiz.questions.filter((qq) => answers[qq.id] !== undefined && answers[qq.id] !== null && answers[qq.id] !== "").length;
    const mm = secondsLeft != null ? String(Math.floor(secondsLeft / 60)).padStart(2, "0") : null;
    const ss = secondsLeft != null ? String(secondsLeft % 60).padStart(2, "0") : null;
    const typeLabel = t(`quiz.types.${q.type}`);

    return (
        <div data-testid="quiz-runner">
            <div className="mb-3 flex items-center justify-between">
                <span className="mono-label text-t4">{t("quiz.question", { i: idx + 1, n: quiz.questions.length })}</span>
                <div className="flex items-center gap-3">
                    {secondsLeft != null && <span className={cn("flex items-center gap-1.5 rounded-lg px-2.5 py-1 font-mono-ui text-xs", secondsLeft < 30 ? "bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))]" : "bg-surface-2 text-t2")}><Clock className="h-3.5 w-3.5" />{mm}:{ss}</span>}
                    <span className="mono-label text-t4">{t("quiz.answered", { n: answeredCount })}</span>
                </div>
            </div>
            <div className="mb-4 h-1 w-full overflow-hidden rounded-full bg-surface-3">
                <div className="h-full rounded-full bg-[hsl(var(--primary))] transition-all duration-300" style={{ width: `${((idx + 1) / quiz.questions.length) * 100}%` }} />
            </div>

            <div className="rounded-2xl border border-border bg-surface-1 p-6">
                <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px] uppercase text-t4">{typeLabel}</span>
                <p className="mt-3 font-reading text-[18px] leading-relaxed text-t1">{q.question}</p>

                <div className="mt-5">
                    {q.type === "mcq" && (
                        <div className="space-y-2">
                            {q.options.map((o, oi) => (
                                <button key={oi} data-testid={`quiz-option-${oi}`} onClick={() => setA(oi)}
                                    className={cn("flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm transition-colors", a === oi ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-t1" : "border-border bg-surface-2 text-t2 hover:bg-surface-3")}>
                                    <span className={cn("flex h-5 w-5 items-center justify-center rounded-full border text-[11px]", a === oi ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]" : "border-border text-t4")}>{String.fromCharCode(65 + oi)}</span>
                                    {o}
                                </button>
                            ))}
                        </div>
                    )}
                    {q.type === "true_false" && (
                        <div className="flex gap-3">
                            {[{ v: true, label: t("quiz.trueLabel"), Icon: Check }, { v: false, label: t("quiz.falseLabel"), Icon: X }].map(({ v, label, Icon }) => (
                                <button key={String(v)} data-testid={`quiz-tf-${v ? "True" : "False"}`} onClick={() => setA(v)}
                                    className={cn("flex flex-1 items-center justify-center gap-2 rounded-xl border py-3 text-sm font-medium transition-colors", a === v ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-t1" : "border-border bg-surface-2 text-t2 hover:bg-surface-3")}>
                                    <Icon className="h-4 w-4" /> {label}
                                </button>
                            ))}
                        </div>
                    )}
                    {["open", "scenario", "case_study", "exam"].includes(q.type) && (
                        <Textarea data-testid="quiz-open-input" value={a || ""} onChange={(e) => setA(e.target.value)} placeholder={t("quiz.typeAnswer")} className="min-h-[120px] resize-none bg-surface-2 text-sm" />
                    )}
                </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
                <Button variant="ghost" className="gap-1 text-t3" disabled={idx === 0} onClick={() => setIdx((i) => Math.max(0, i - 1))}><ChevronLeft className="h-4 w-4" /> {t("quiz.back")}</Button>
                {idx < quiz.questions.length - 1 ? (
                    <Button data-testid="quiz-next-button" className="gap-1" onClick={() => setIdx((i) => i + 1)}>{t("quiz.next")} <ChevronRight className="h-4 w-4" /></Button>
                ) : (
                    <Button data-testid="quiz-submit-button" className="gap-2" disabled={phase === "submitting"} onClick={submit}>
                        {phase === "submitting" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {t("quiz.submit")}
                    </Button>
                )}
            </div>
        </div>
    );
};
